import Button from '@jetbrains/ring-ui/components/button/button'
import classNames from 'classnames'
import {memo, useCallback, useState} from 'react'

import SvgIcon from '../../../../../../common/SvgIcon/SvgIcon'
import TruncateHtml from '../../../../../../common/TruncateHtml/TruncateHtml'
import {useBanners} from '../../../../contexts/Banners/BannersContext'
import {usePipelineId} from '../../../../hooks/pipeline'

import type {BannerProps} from './Banner.types'
import {bannerIcons} from './Banner.types'
import BannerDialog from './BannerDialog/BannerDialog'

import styles from './Banner.css'

function Banner({id, type, title, message, closeable, actions}: BannerProps) {
  const pipelineId = usePipelineId()
  const [truncated, setTruncated] = useState<boolean>(false)
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const {removeBanner} = useBanners()
  const withDialog = (title && message) || truncated

  const onTruncateReady = useCallback((value: boolean) => {
    setTruncated(value)
  }, [])

  const onShowMore = useCallback(() => {
    setShowDialog(true)
  }, [])

  const onClose = useCallback(() => {
    if (closeable) {
      removeBanner(pipelineId, id)
    }
    setShowDialog(false)
  }, [pipelineId, id, closeable, removeBanner])

  const onGotIt = useCallback(() => {
    removeBanner(pipelineId, id)
  }, [pipelineId, id, removeBanner])

  return (
    <div className={classNames(styles.container, styles[type])}>
      <SvgIcon className={styles.icon} icon={bannerIcons[type]} />
      <TruncateHtml
        clamp={1}
        withToggleButton={false}
        lineHeight={16}
        className={styles.message}
        onReady={onTruncateReady}
      >
        {title || message}
      </TruncateHtml>
      <div className={styles.actions}>
        {withDialog && (
          <Button text className={styles.action} onClick={onShowMore}>
            {'Show more'}
          </Button>
        )}
        {closeable && (
          <Button text className={styles.action} onClick={onGotIt}>
            {'Got it'}
          </Button>
        )}
        {actions?.map(({label, onClick}, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Button key={index} text className={styles.action} onClick={onClick}>
            {label}
          </Button>
        ))}
      </div>
      {withDialog && (
        <BannerDialog show={showDialog} title={title} message={message} onClose={onClose} />
      )}
    </div>
  )
}
export default memo(Banner)
